import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  IOnuOption,
  IOnuView,
  IOnuViewEthernetPort,
  IOnuViewWifiPort,
  OnuAction,
  OnuReadResource,
} from '@modules/clients/services/olt/models/onu-view.model';
import { OnuViewService } from '@modules/clients/services/olt/onu-view.service';
import { ToastrService } from 'ngx-toastr';
import { OnuTextInputDirective } from '../onu-text-input/onu-text-input.directive';
import {
  invalidOnuTextChars,
  invalidOnuTextMessage,
  ONU_TEXT_ALLOWED_HINT,
} from '../onu-text-input/onu-text.util';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DropdownModule } from 'primeng/dropdown';
import { InputTextModule } from 'primeng/inputtext';
import { MultiSelectModule } from 'primeng/multiselect';
import { RadioButtonModule } from 'primeng/radiobutton';

/** Opción de una lista de SmartOLT; raw conserva el registro original (p. ej. datos del pool IP). */
export interface IOnuListOption extends IOnuOption {
  selected?: boolean;
  raw?: any;
}

export type OnuEditForm =
  | 'external_id'
  | 'location'
  | 'onu_type'
  | 'sn'
  | 'move'
  | 'allocated'
  | 'gpon_type'
  | 'speed'
  | 'mode'
  | 'mgmt'
  | 'vlans'
  | 'port'
  | 'wifi';

export const ONU_EDIT_TITLES: Record<OnuEditForm, string> = {
  external_id: 'Actualizar ID externo',
  location: 'Ubicación y datos del cliente',
  onu_type: 'Cambiar tipo de ONU',
  sn: 'Reemplazar ONU por SN',
  move: 'Mover ONU',
  allocated: 'Cambiar ONU ID asignado',
  gpon_type: 'Canal GPON',
  speed: 'Perfiles de velocidad',
  mode: 'Modo de la ONU y WAN',
  mgmt: 'Gestión (TR069, Mgmt IP) y VoIP',
  vlans: 'VLANs adjuntas',
  port: 'Configurar puerto Ethernet',
  wifi: 'Configurar WiFi',
};

/** Acción del backend que guarda cada formulario. */
const FORM_ACTIONS: Record<OnuEditForm, OnuAction> = {
  external_id: 'update_external_id',
  location: 'update_location',
  onu_type: 'change_onu_type',
  sn: 'update_sn',
  move: 'move_onu',
  allocated: 'change_allocated_onu',
  gpon_type: 'update_gpon_type',
  speed: 'update_speed_profiles',
  mode: 'update_mode',
  mgmt: 'update_mgmt_ip',
  vlans: 'update_extra_vlans',
  port: 'update_port',
  wifi: 'update_wifi_port',
};

/**
 * Formularios de edición de la pantalla onu/view de SmartOLT. Cada uno envía los mismos
 * campos que el original; las listas (zonas, VLANs, OLTs…) se piden a SmartOLT al abrirlo.
 */
@Component({
  selector: 'app-onu-edit-form',
  standalone: true,
  imports: [
    FormsModule,
    ButtonModule,
    CheckboxModule,
    DropdownModule,
    InputTextModule,
    MultiSelectModule,
    RadioButtonModule,
    OnuTextInputDirective,
  ],
  templateUrl: './onu-edit-form.component.html',
})
export class OnuEditFormComponent implements OnChanges {
  @Input({ required: true }) view!: IOnuView;
  @Input({ required: true }) form!: OnuEditForm;
  /** puerto a configurar cuando form = 'port' */
  @Input() port: IOnuViewEthernetPort | null = null;
  /** SSID a configurar (o puerto libre para "Agregar SSID") cuando form = 'wifi' */
  @Input() wifiPort: IOnuViewWifiPort | null = null;
  @Output() saved = new EventEmitter<string>();
  @Output() cancelled = new EventEmitter<void>();

  public model: Record<string, any> = {};
  /** listas pedidas a SmartOLT; una lista no existe hasta que llega */
  public lists: Partial<Record<string, IOnuListOption[]>> = {};
  public loading: Record<string, boolean> = {};
  public saving = false;

  public readonly portModes = ['LAN', 'Access', 'Hybrid', 'Trunk', 'Transparent'];
  public readonly wifiPortModes = ['LAN', 'Access', 'Hybrid', 'Trunk'];
  public readonly authModes: IOnuOption[] = [
    { value: 'wpa2', label: 'WPA2' },
    { value: 'auto', label: 'Auto' },
    { value: 'open_system', label: 'Abierta' },
  ];
  /** error por campo cuando se intenta escribir un carácter que SmartOLT rechaza */
  public fieldErrors: Record<string, string> = {};
  public readonly textHint = ONU_TEXT_ALLOWED_HINT;
  public passwordVisible = false;
  public passwordLoading = false;
  public readonly routerModes = ['Setup via ONU webpage', 'DHCP', 'Static', 'PPPoE'];
  public readonly gponTypes: IOnuOption[] = [
    { value: 'gpon', label: 'GPON' },
    { value: 'xgpon', label: 'XG-PON' },
    { value: 'xgspon', label: 'XGS-PON' },
  ];

  constructor(
    private readonly onuViewService: OnuViewService,
    private readonly toastService: ToastrService,
  ) {}

  ngOnChanges(): void {
    if (this.view && this.form) {
      this.init();
    }
  }

  private get ponType(): string {
    return this.view.onu.channelType || 'gpon';
  }

  private init() {
    const v = this.view;
    this.lists = {};
    switch (this.form) {
      case 'external_id':
        this.model = { client_external_id: v.externalId };
        break;
      case 'location':
        this.model = this.locationModel();
        this.load('zones', 'zones');
        this.onZoneChange(false);
        break;
      case 'onu_type':
        this.model = {
          onu_type_id: v.onuType.id,
          custom_template_id: v.onuType.customTemplateId || '0',
        };
        this.load('onu_types', 'onu_types', { ponType: this.ponType });
        break;
      case 'sn':
        this.model = { sn: '', new_sn_onu_type_id: v.onuType.id };
        this.load('onu_types', 'onu_types', { ponType: this.ponType });
        break;
      case 'move':
        this.model = {
          change_olt_id: v.olt.id,
          change_board: v.board,
          change_port: v.port.value,
          move_to_default_vlan: false,
        };
        this.load('olts', 'olts');
        this.onOltChange(false);
        break;
      case 'allocated':
        this.model = { target_onu_id: '' };
        this.load('available_onu_ids', 'available_onu_ids', {
          oltId: v.olt.id,
          board: v.board,
          port: v.port.value,
          channelType: this.ponType,
          currentOnuId: v.onu.currentOnuId,
        });
        break;
      case 'gpon_type':
        this.model = {
          gpon_type: v.gponType.value || 'gpon',
          detected_max_onu_id_override_mode: 'low',
        };
        break;
      case 'speed':
        this.model = speedModel(v.speedProfiles);
        break;
      case 'mode':
        this.model = this.modeModel();
        this.load('vlans_default', 'vlans_default');
        break;
      case 'mgmt':
        this.model = this.mgmtModel();
        this.load('vlans_mgmt', 'vlans_mgmt', { oltId: v.olt.id });
        this.load('mgmt_ips', 'mgmt_ips', { oltId: v.olt.id, scope: 'mgmt' });
        break;
      case 'vlans':
        // las VLANs ya adjuntas (con la principal) vienen en el script de la página de SmartOLT
        this.model = { extra_vlans: [...v.attachedVlanIds] };
        this.load('vlans_extra', 'vlans_extra', { oltId: v.olt.id });
        break;
      case 'wifi':
        this.passwordVisible = false;
        this.model = this.wifiModel();
        this.load('vlans_ethernet', 'vlans_ethernet');
        break;
      case 'port':
        this.model = this.portModel();
        this.load('vlans_ethernet', 'vlans_ethernet');
        break;
    }
  }

  private locationModel(): Record<string, any> {
    const l = this.view.location;
    return {
      zone_id: l.zoneId,
      odb_id: l.odbId,
      odb_port: l.odbPort,
      sector_id: l.sectorId === '0' ? '' : l.sectorId,
      location_name: l.name,
      description: l.description,
      contact: l.contact,
      latitude: l.latitude,
      longitude: l.longitude,
    };
  }

  private modeModel(): Record<string, any> {
    const mode = this.view.mode;
    const value = (key: string) => mode[key] ?? '';
    return {
      default_vlan_id: value('defaultVlanId'),
      mode: mode['mode'] || 'Routing',
      router_mode: mode['routerMode'] || 'Setup via ONU webpage',
      configuration_method: mode['configurationMethod'] || 'OMCI',
      ip_address: value('ipAddress'),
      subnet_mask: value('subnetMask'),
      gateway: value('gateway'),
      dns1: value('dns1'),
      dns2: value('dns2'),
      username: value('username'),
      pppoePassword: value('password'),
      ip_dhcp_snooping: true,
      allow_remote_access: mode['allowRemoteAccess'] ?? '0',
      wan_ip_source: 'manual',
    };
  }

  private mgmtModel(): Record<string, any> {
    const mgmt = this.view.mgmtIp;
    const value = (key: string) => mgmt[key] ?? '';
    const tr069Profile = this.view.options['tr069_profile_id']?.[1]?.value ?? '0';
    return {
      tr069_profile_id: mgmt['tr069Enabled'] === '1' ? tr069Profile : '0',
      tr069_interface: 'mgmt',
      mgmt_ip_mode: mgmt['mgmtIpMode'] || 'Inactive',
      mgmt_ip_allow_remote_access: false,
      mgmt_ip_vlan_id: value('mgmtIpVlanId'),
      mgmt_ip_address: value('mgmtIpAddress'),
      mgmt_ip_subnet_mask: value('mgmtIpSubnetMask'),
      mgmt_ip_default_gateway: value('mgmtIpDefaultGateway'),
      mgmt_ip_dns1: value('mgmtIpDns1'),
      mgmt_ip_dns2: value('mgmtIpDns2'),
      voip_mode: mgmt['voipMode'] || 'Disabled',
      voip_attach_to: mgmt['voipAttachTo'] || 'Mgmt',
    };
  }

  private wifiModel(): Record<string, any> {
    const w = this.wifiPort;
    return {
      wifi_port_id: w?.id ?? '',
      wifi_tr069_slot: w?.tr069Slot ?? '',
      // "Agregar SSID" abre un puerto deshabilitado: se habilita al guardarlo
      wifi_port_is_enabled: w?.state === '0' && w?.ssid ? '0' : '1',
      wifi_port_mode: w?.portMode || 'LAN',
      wifi_vlan_id: vlanOrEmpty(w?.vlanId),
      wifi_port_extra_vlans: listOf(w?.extraVlans),
      ssid: w?.ssid ?? '',
      auth_mode: w?.authMode || 'wpa2',
      wifi_password: '',
      wifi_vlan_dhcp: w?.vlanDhcp || 'No control',
    };
  }

  private portModel(): Record<string, any> {
    const p = this.port;
    const defaultMode = p?.onuMode === 'Bridging' ? 'Access' : 'LAN';
    return {
      network_port_id: p?.id ?? '',
      is_enabled: p?.state === '0' ? '0' : '1',
      port_mode: p?.portMode || defaultMode,
      vlan_id: vlanOrEmpty(p?.vlanId),
      port_extra_vlans: listOf(p?.extraVlans),
      vlan_dhcp: p?.vlanDhcp || 'No control',
    };
  }

  /**
   * load: pide una lista a SmartOLT y la normaliza a {value,label}
   */
  public async load(
    key: string,
    resource: OnuReadResource,
    params: Record<string, string> = {},
  ): Promise<IOnuListOption[]> {
    this.loading[key] = true;
    try {
      const response = await this.onuViewService.read(this.view.onuId, resource, params);
      const list = this.toOptions(response.data);
      this.lists[key] = list;
      return list;
    } catch (error) {
      this.lists[key] = [];
      return [];
    } finally {
      this.loading[key] = false;
    }
  }

  private toOptions(data: any): IOnuListOption[] {
    const list: any[] = Array.isArray(data?.response)
      ? data.response
      : Array.isArray(data)
      ? data
      : [];
    return list.map((item) => {
      if (typeof item !== 'object' || item === null) {
        return { value: String(item), label: String(item) };
      }
      const value = item.id ?? item.key ?? item.value;
      const label = item.name ?? item.label ?? item.text ?? item.vlan ?? item.value ?? value;
      return {
        value: String(value ?? ''),
        label: String(label ?? '').trim(),
        selected: item.group_id === 1,
        raw: item,
      };
    });
  }

  public onZoneChange(resetOdb = true) {
    if (resetOdb) {
      this.model['odb_id'] = '';
      this.model['odb_port'] = '';
      this.model['sector_id'] = '';
    }
    if (this.model['zone_id']) {
      this.load('odbs', 'odbs', { zoneId: this.model['zone_id'] });
    }
    this.onOdbChange(false);
  }

  public onOdbChange(reset = true) {
    if (reset) {
      this.model['odb_port'] = '';
      this.model['sector_id'] = '';
    }
    const odbId = this.model['odb_id'] || '0';
    this.load('sectors', 'sectors', { odbId });
    if (odbId !== '0') {
      this.load('odb_ports', 'odb_ports', { odbId, odbPort: this.model['odb_port'] || '0' });
    } else {
      this.lists['odb_ports'] = [];
    }
  }

  public onOltChange(reset = true) {
    if (reset) {
      this.model['change_board'] = '';
      this.model['change_port'] = '';
      this.lists['olt_ports'] = [];
    }
    if (this.model['change_olt_id']) {
      this.load('olt_boards', 'olt_boards', { oltId: this.model['change_olt_id'] });
    }
    if (!reset) this.onBoardChange(false);
  }

  public onBoardChange(reset = true) {
    if (reset) this.model['change_port'] = '';
    if (this.model['change_olt_id'] && this.model['change_board']) {
      this.load('olt_ports', 'olt_ports', {
        oltId: this.model['change_olt_id'],
        board: this.model['change_board'],
      });
    }
  }

  /** El campo bloqueó caracteres no válidos: se explica cuáles. */
  public onInvalidChars(field: string, chars: string[]) {
    this.fieldErrors[field] = invalidOnuTextMessage(chars);
  }

  /** El usuario volvió a escribir algo válido: se quita el error. */
  public clearFieldError(field: string) {
    delete this.fieldErrors[field];
  }

  /**
   * validateTexts: red de seguridad por si un valor llegó sin pasar por el teclado
   * (autocompletado, datos precargados). SmartOLT los ignoraría sin avisar.
   */
  private validateTexts(fields: string[], model: Record<string, any>): boolean {
    let valid = true;
    fields.forEach((field) => {
      const bad = invalidOnuTextChars(model[field]);
      if (bad.length) {
        this.fieldErrors[field] = invalidOnuTextMessage(bad);
        valid = false;
      } else {
        delete this.fieldErrors[field];
      }
    });
    return valid;
  }

  /**
   * showWifiPassword: lee la contraseña actual del SSID (solo si el campo está vacío)
   */
  public async showWifiPassword() {
    this.passwordVisible = !this.passwordVisible;
    if (this.passwordVisible) await this.loadWifiPassword();
  }

  private async loadWifiPassword() {
    if (this.model['wifi_password'] || !this.model['wifi_port_id']) return;
    this.passwordLoading = true;
    try {
      const response = await this.onuViewService.read<{ status: string; password?: string }>(
        this.view.onuId,
        'wifi_password',
        { wifi_port_id: this.model['wifi_port_id'], tr069_slot: this.model['wifi_tr069_slot'] },
      );
      if (response.data?.status === 'ok' && response.data.password) {
        this.model['wifi_password'] = response.data.password;
      }
    } catch {
      // sin contraseña actual: el usuario la escribe a mano
    } finally {
      this.passwordLoading = false;
    }
  }

  /** "Clear WiFi port settings": vacía SSID y contraseña antes de guardar. */
  public clearWifi() {
    this.model['ssid'] = '';
    this.model['wifi_password'] = '';
    this.passwordVisible = false;
  }

  /** Al elegir una IP del pool, SmartOLT completa máscara, gateway y DNS. */
  public onMgmtIpChange() {
    const raw = this.lists['mgmt_ips']?.find((o) => o.value === this.model['mgmt_ip_address'])?.raw;
    if (raw) {
      this.model['mgmt_ip_subnet_mask'] = raw.subnet_mask ?? '';
      this.model['mgmt_ip_default_gateway'] = raw.default_gateway ?? '';
      this.model['mgmt_ip_dns1'] = raw.dns1 ?? '';
      this.model['mgmt_ip_dns2'] = raw.dns2 ?? '';
    }
  }

  /**
   * save: envía el formulario con los campos que espera SmartOLT
   */
  public async save() {
    if (this.form === 'wifi' && this.model['ssid'] && this.model['auth_mode'] !== 'open_system') {
      // SmartOLT exige la contraseña al guardar: si no se cambió, se reenvía la actual
      await this.loadWifiPassword();
    }
    const body = this.buildBody();
    if (!body) return;
    this.saving = true;
    try {
      if (this.form === 'sn') {
        const duplicate = await this.onuViewService.read<{ response: boolean }>(
          this.view.onuId,
          'is_duplicate_sn',
          {
            sn: body['sn'],
          },
        );
        if (duplicate.data?.response) {
          this.toastService.error(`El SN ${body['sn']} ya está configurado en SmartOLT`);
          return;
        }
      }
      const response = await this.onuViewService.action(
        this.view.onuId,
        FORM_ACTIONS[this.form],
        body,
      );
      this.saved.emit(response.message);
    } catch (error) {
      // el interceptor ya mostró el error de SmartOLT
    } finally {
      this.saving = false;
    }
  }

  /** Cuerpo que se envía a SmartOLT; null si hay algo que corregir (ya avisado). */
  private buildBody(): Record<string, any> | null {
    const m = { ...this.model };
    switch (this.form) {
      case 'location':
        return this.locationBody(m);
      case 'sn':
        return this.snBody(m);
      case 'mode':
        return modeBody(m);
      case 'speed':
        m['use_svlan'] = checkbox(m['use_svlan']);
        m['use_other_all_tls_vlan'] = checkbox(m['use_other_all_tls_vlan']);
        return m;
      case 'mgmt':
        m['mgmt_ip_allow_remote_access'] = checkbox(m['mgmt_ip_allow_remote_access']);
        return m;
      case 'move':
        m['move_to_default_vlan'] = checkbox(m['move_to_default_vlan']);
        return m;
      case 'wifi':
        return this.wifiBody(m);
      case 'port':
        return portBody(m);
      case 'vlans':
        return this.vlansBody(m);
      default:
        return m;
    }
  }

  private locationBody(m: Record<string, any>) {
    if (!this.validateTexts(['location_name', 'description', 'contact'], m)) {
      this.toastService.error('Corrija los caracteres no válidos antes de guardar');
      return null;
    }
    return m;
  }

  private snBody(m: Record<string, any>) {
    m['sn'] = String(m['sn'] ?? '')
      .trim()
      .toUpperCase();
    if (!m['sn']) {
      this.toastService.error('Ingrese el nuevo SN');
      return null;
    }
    return m;
  }

  private wifiBody(m: Record<string, any>) {
    const enabled = m['wifi_port_is_enabled'] === '1';
    const needsPassword = enabled && m['ssid'] && m['auth_mode'] !== 'open_system';
    // misma regla que SmartOLT: 8-32 caracteres ASCII imprimibles, sin & ni espacios
    if (needsPassword && !/^[!-%'-~]{8,32}$/.test(m['wifi_password'])) {
      this.toastService.error(
        'La contraseña WiFi debe tener 8 a 32 caracteres, sin espacios ni &.',
      );
      return null;
    }
    if (['Access', 'Hybrid'].includes(m['wifi_port_mode']) && !m['wifi_vlan_id'] && enabled) {
      this.toastService.error('Seleccione la VLAN del SSID');
      return null;
    }
    return m;
  }

  private vlansBody(m: Record<string, any>) {
    const vlans = new Set<string>([...(m['extra_vlans'] ?? [])]);
    if (this.view.mainVlanDbId) vlans.add(this.view.mainVlanDbId);
    if (!vlans.size) {
      this.toastService.error('Seleccione al menos una VLAN');
      return null;
    }
    if (this.view.maxVlans && vlans.size > this.view.maxVlans) {
      this.toastService.error(
        `SmartOLT admite como máximo ${this.view.maxVlans} VLANs por ONU, incluida la principal.`,
      );
      return null;
    }
    // la VLAN principal siempre va adjunta
    return { ...m, extra_vlans: [...vlans] };
  }
}

/** Casilla marcada: SmartOLT espera '1'; desmarcada no se envía. */
const checkbox = (checked: unknown): '1' | undefined => (checked ? '1' : undefined);

/**
 * Formulario de velocidad. En las OLT con service-port (C600) SmartOLT reenvía además el
 * service-port y sus opciones de SVLAN tal como están; en las demás solo van los perfiles.
 */
const speedModel = (speed: IOnuView['speedProfiles']): Record<string, unknown> => {
  const model: Record<string, unknown> = {
    download_speed: speed?.downloadId ?? '',
    upload_speed: speed?.uploadId ?? '',
  };
  if (!speed?.servicePort) return model;
  return {
    ...model,
    service_port: speed.servicePort,
    use_svlan: speed.useSvlan === '1',
    ...(speed.useSvlan === '1' ? { svlan_id: speed.svlanId ?? '' } : {}),
    use_other_all_tls_vlan: speed.useOtherAllTlsVlan === '1',
  };
};

/** VLAN de SmartOLT: '0' o vacío es "sin VLAN". */
const vlanOrEmpty = (vlan: string | undefined): string => (vlan && vlan !== '0' ? vlan : '');

const listOf = (csv: string | undefined): string[] => (csv ? csv.split(',') : []);

/** Modo WAN: solo se envían los campos del modo elegido. */
const modeBody = (m: Record<string, any>) => {
  m['ip_dhcp_snooping'] = checkbox(m['ip_dhcp_snooping']);
  if (m['mode'] === 'Bridging') {
    delete m['router_mode'];
    delete m['configuration_method'];
  }
  if (m['router_mode'] !== 'Static') {
    ['wan_ip_source', 'ip_address', 'subnet_mask', 'gateway', 'dns1', 'dns2'].forEach(
      (field) => delete m[field],
    );
  }
  if (m['router_mode'] !== 'PPPoE') {
    delete m['username'];
    delete m['pppoePassword'];
  }
  return m;
};

/** Puerto Ethernet: LAN y Transparent no llevan VLAN; solo Hybrid y Trunk llevan VLANs adicionales. */
const portBody = (m: Record<string, any>) => {
  if (['LAN', 'Transparent'].includes(m['port_mode'])) {
    m['vlan_id'] = '';
    m['port_extra_vlans'] = [];
  } else if (!['Hybrid', 'Trunk'].includes(m['port_mode'])) {
    m['port_extra_vlans'] = [];
  }
  return m;
};
